import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabaseClient';
import { 
  Calendar as CalendarIcon, 
  Clock, 
  Wifi, 
  Key, 
  Coffee, 
  Wind, 
  Trash2, 
  LogOut, 
  Plus, 
  User, 
  Info, 
  Loader2, 
  AlertTriangle,
  Sparkles,
  MapPin,
  Heart,
  Users,
  Check,
  X,
  Shield,
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  ChevronDown,
  Repeat
} from 'lucide-react';

interface Booking {
  id: string;
  user_id: string;
  title: string;
  start_time: string;
  end_time: string;
  user_name: string;
  created_at: string;
  recurrence_group_id?: string | null;
}

interface PendingProfile {
  id: string;
  email: string;
  full_name: string;
  created_at: string;
  role: string;
  approved: boolean;
}

type CalendarViewMode = 'day' | '4days' | 'week' | 'month' | 'year' | 'schedule';

const getMonday = (d: Date) => {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(date.setDate(diff));
};

const getDaysOfWeek = (monday: Date) => {
  const days = [];
  for (let i = 0; i < 7; i++) {
    const day = new Date(monday);
    day.setDate(monday.getDate() + i);
    days.push(day);
  }
  return days;
};

const isSameDay = (d1: Date, d2: Date) => {
  return d1.getFullYear() === d2.getFullYear() &&
         d1.getMonth() === d2.getMonth() &&
         d1.getDate() === d2.getDate();
};

const toLocalDateString = (d: Date) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const getDaysInMonth = (year: number, month: number) => {
  return new Date(year, month + 1, 0).getDate();
};

export default function Cowork() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [profile, setProfile] = useState<any>(null);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [pendingProfiles, setPendingProfiles] = useState<PendingProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [viewMode, setViewMode] = useState<'agenda' | 'admin'>('agenda');
  const [calendarView, setCalendarView] = useState<CalendarViewMode>('day');
  const [showViewDropdown, setShowViewDropdown] = useState(false);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [date, setDate] = useState(() => toLocalDateString(new Date()));
  const [currentWeekStart, setCurrentWeekStart] = useState<Date>(() => getMonday(new Date()));
  const [currentMonth, setCurrentMonth] = useState<{ year: number; month: number }>(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });
  const [currentYear, setCurrentYear] = useState<number>(() => new Date().getFullYear());
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('10:00');
  const [title, setTitle] = useState('');
  const [recurrence, setRecurrence] = useState<'none' | 'weekly' | 'biweekly'>('none');
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');

  // Modal de exclusão de recorrentes
  const [deleteModal, setDeleteModal] = useState<{ bookingId: string; groupId: string | null } | null>(null);

  useEffect(() => {
    const fetchUserDataAndBookings = async () => {
      try {
        setLoading(true);
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) { navigate('/cowork/login'); return; }
        setCurrentUser(session.user);
        const { data: profileData, error: profileError } = await supabase
          .from('profiles').select('*').eq('id', session.user.id).single();
        if (profileError) {
          console.error('Erro ao buscar perfil:', profileError);
        } else {
          setProfile(profileData);
          if (profileData?.role === 'admin') await fetchPendingProfiles();
        }
        if (window.innerWidth < 768) setCalendarView('schedule');
        await fetchBookings();
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchUserDataAndBookings();
  }, [navigate]);

  const fetchBookings = async () => {
    try {
      const now = new Date();
      const startRange = new Date(now.setDate(now.getDate() - 60)).toISOString();
      const { data, error } = await supabase
        .from('bookings').select('*')
        .gte('start_time', startRange)
        .order('start_time', { ascending: true });
      if (error) console.error('Erro ao buscar agendamentos:', error);
      else setBookings(data || []);
    } catch (err) { console.error(err); }
  };

  const fetchPendingProfiles = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles').select('*').eq('approved', false).order('created_at', { ascending: true });
      if (error) console.error('Erro ao buscar perfis pendentes:', error);
      else setPendingProfiles(data || []);
    } catch (err) { console.error(err); }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/cowork/login');
  };

  const handlePrev = () => {
    if (calendarView === 'day' || calendarView === 'schedule') {
      const d = new Date(`${date}T12:00:00`);
      d.setDate(d.getDate() - 1);
      setDate(toLocalDateString(d));
    } else if (calendarView === '4days') {
      const d = new Date(`${date}T12:00:00`);
      d.setDate(d.getDate() - 4);
      setDate(toLocalDateString(d));
    } else if (calendarView === 'week') {
      const d = new Date(currentWeekStart);
      d.setDate(d.getDate() - 7);
      setCurrentWeekStart(d);
      setDate(toLocalDateString(d));
    } else if (calendarView === 'month') {
      const m = currentMonth.month === 0 ? 11 : currentMonth.month - 1;
      const y = currentMonth.month === 0 ? currentMonth.year - 1 : currentMonth.year;
      setCurrentMonth({ year: y, month: m });
    } else if (calendarView === 'year') {
      setCurrentYear(y => y - 1);
    }
    setFormSuccess(''); setFormError('');
  };

  const handleNext = () => {
    if (calendarView === 'day' || calendarView === 'schedule') {
      const d = new Date(`${date}T12:00:00`);
      d.setDate(d.getDate() + 1);
      setDate(toLocalDateString(d));
    } else if (calendarView === '4days') {
      const d = new Date(`${date}T12:00:00`);
      d.setDate(d.getDate() + 4);
      setDate(toLocalDateString(d));
    } else if (calendarView === 'week') {
      const d = new Date(currentWeekStart);
      d.setDate(d.getDate() + 7);
      setCurrentWeekStart(d);
      setDate(toLocalDateString(d));
    } else if (calendarView === 'month') {
      const m = currentMonth.month === 11 ? 0 : currentMonth.month + 1;
      const y = currentMonth.month === 11 ? currentMonth.year + 1 : currentMonth.year;
      setCurrentMonth({ year: y, month: m });
    } else if (calendarView === 'year') {
      setCurrentYear(y => y + 1);
    }
    setFormSuccess(''); setFormError('');
  };

  const handleToday = () => {
    const today = new Date();
    setDate(toLocalDateString(today));
    setCurrentWeekStart(getMonday(today));
    setCurrentMonth({ year: today.getFullYear(), month: today.getMonth() });
    setCurrentYear(today.getFullYear());
    setFormSuccess(''); setFormError('');
  };

  const dropdownTimes: string[] = [];
  for (let hour = 9; hour <= 22; hour++) {
    const hourStr = hour.toString().padStart(2, '0');
    dropdownTimes.push(`${hourStr}:00`);
    if (hour < 22) dropdownTimes.push(`${hourStr}:30`);
  }

  const gridHours = [
    '09:00','10:00','11:00','12:00','13:00','14:00',
    '15:00','16:00','17:00','18:00','19:00','20:00','21:00'
  ];

  const handleAddBooking = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(''); setFormSuccess('');
    if (!currentUser || !profile) return;

    const startDateTime = new Date(`${date}T${startTime}:00`);
    const endDateTime = new Date(`${date}T${endTime}:00`);
    if (endDateTime <= startDateTime) { setFormError('A hora de término deve ser posterior à hora de início.'); return; }
    if (startDateTime < new Date()) { setFormError('Não é possível agendar um horário no passado.'); return; }

    setActionLoading(true);

    // Gerar lista de ocorrências
    const occurrences: { start: Date; end: Date }[] = [];
    if (recurrence === 'none') {
      occurrences.push({ start: startDateTime, end: endDateTime });
    } else {
      const intervalDays = recurrence === 'weekly' ? 7 : 14;
      const count = recurrence === 'weekly' ? 12 : 6;
      for (let i = 0; i < count; i++) {
        const s = new Date(startDateTime); s.setDate(s.getDate() + i * intervalDays);
        const en = new Date(endDateTime); en.setDate(en.getDate() + i * intervalDays);
        occurrences.push({ start: s, end: en });
      }
    }

    // Verificar conflitos de forma eficiente (bookings já está ordenado por data/hora de início)
    const hasConflict = occurrences.some(occ => {
      const occStart = occ.start.getTime();
      const occEnd = occ.end.getTime();
      
      for (let i = 0; i < bookings.length; i++) {
        const b = bookings[i];
        const bStart = new Date(b.start_time).getTime();
        
        // Como o array de bookings é ordenado por start_time, se o booking iniciar após a ocorrência atual acabar,
        // nenhum outro booking subsequente entrará em conflito com esta ocorrência. Podemos dar break.
        if (bStart >= occEnd) {
          break;
        }
        
        const bEnd = new Date(b.end_time).getTime();
        if (occStart < bEnd && occEnd > bStart) {
          return true; // Encontrou conflito
        }
      }
      return false;
    });

    if (hasConflict) {
      setFormError('Conflito de Horário! Uma ou mais ocorrências colidem com reservas existentes.');
      setActionLoading(false); return;
    }

    try {
      const groupId = recurrence !== 'none'
        ? (globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`)
        : null;

      const rows = occurrences.map(occ => ({
        user_id: currentUser.id,
        title: title.trim() || 'Atendimento',
        start_time: occ.start.toISOString(),
        end_time: occ.end.toISOString(),
        user_name: profile.full_name || currentUser.email,
        recurrence_group_id: groupId,
      }));

      const { error } = await supabase.from('bookings').insert(rows);
      if (error) { setFormError(error.message); }
      else {
        const msg = recurrence === 'none'
          ? 'Horário reservado com sucesso!'
          : `${occurrences.length} reservas criadas com sucesso!`;
        setFormSuccess(msg);
        setTitle('');
        setRecurrence('none');
        await fetchBookings();
        setTimeout(() => { setIsBookingModalOpen(false); setFormSuccess(''); }, 1000);
      }
    } catch (err: any) {
      setFormError('Ocorreu um erro ao salvar o agendamento.');
    } finally { setActionLoading(false); }
  };

  const handleDeleteBooking = (bookingId: string, groupId?: string | null) => {
    if (groupId) {
      // Abre modal de escolha para reservas recorrentes
      setDeleteModal({ bookingId, groupId });
    } else {
      // Exclusão direta para reservas únicas
      confirmDeleteSingle(bookingId);
    }
  };

  const confirmDeleteSingle = async (bookingId: string) => {
    setDeleteModal(null);
    setActionLoading(true);
    try {
      const { error } = await supabase.from('bookings').delete().eq('id', bookingId);
      if (error) alert('Erro ao excluir reserva: ' + error.message);
      else await fetchBookings();
    } catch (err) { console.error(err); } finally { setActionLoading(false); }
  };

  const confirmDeleteAll = async (groupId: string) => {
    setDeleteModal(null);
    setActionLoading(true);
    try {
      const { error } = await supabase.from('bookings').delete().eq('recurrence_group_id', groupId);
      if (error) alert('Erro ao excluir série: ' + error.message);
      else await fetchBookings();
    } catch (err) { console.error(err); } finally { setActionLoading(false); }
  };

  const handleApproveProfile = async (profileId: string) => {
    setActionLoading(true);
    try {
      const { error } = await supabase.from('profiles').update({ approved: true }).eq('id', profileId);
      if (error) alert('Erro ao aprovar usuário: ' + error.message);
      else await fetchPendingProfiles();
    } catch (err) { console.error(err); } finally { setActionLoading(false); }
  };

  const handleRejectProfile = async (profileId: string) => {
    if (!window.confirm('Tem certeza que deseja recusar e excluir o cadastro deste profissional?')) return;
    setActionLoading(true);
    try {
      const { error } = await supabase.from('profiles').delete().eq('id', profileId);
      if (error) alert('Erro ao recusar usuário: ' + error.message);
      else await fetchPendingProfiles();
    } catch (err) { console.error(err); } finally { setActionLoading(false); }
  };

  const handleCellClick = (selectedDay: Date, hourStr: string) => {
    setDate(toLocalDateString(selectedDay));
    setStartTime(hourStr);
    const [h, m] = hourStr.split(':').map(Number);
    const endH = h + 1;
    const endHStr = String(endH).padStart(2, '0') + ':' + String(m).padStart(2, '0');
    setEndTime(endH < 22 || (endH === 22 && m === 0) ? endHStr : '22:00');
    setFormSuccess(''); setFormError('');
    setIsBookingModalOpen(true);
  };

  const getBookingLayoutStyles = (booking: Booking) => {
    const start = new Date(booking.start_time);
    const end = new Date(booking.end_time);
    const startMins = (start.getHours() - 9) * 60 + start.getMinutes();
    const endMins = (end.getHours() - 9) * 60 + end.getMinutes();
    // Janela de 9h às 22h = 13 horas = 780 minutos
    const clampedStart = Math.max(0, Math.min(780, startMins));
    const clampedEnd = Math.max(0, Math.min(780, endMins));
    return {
      top: `${(clampedStart / 780) * 100}%`,
      height: `${((clampedEnd - clampedStart) / 780) * 100}%`
    };
  };

  const formatTimeLabel = (isoString: string) =>
    new Date(isoString).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

  const formatDateLabel = (d: Date) =>
    d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });

  const isAdmin = profile?.role === 'admin';

  const calendarTitle = (() => {
    const d = new Date(`${date}T12:00:00`);
    if (calendarView === 'day' || calendarView === 'schedule')
      return d.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
    if (calendarView === '4days') {
      const end = new Date(d); end.setDate(end.getDate() + 3);
      return `${formatDateLabel(d)} – ${formatDateLabel(end)} de ${d.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}`;
    }
    if (calendarView === 'week') {
      const days = getDaysOfWeek(currentWeekStart);
      return `${formatDateLabel(days[0])} – ${formatDateLabel(days[6])} de ${days[0].toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}`;
    }
    if (calendarView === 'month')
      return new Date(currentMonth.year, currentMonth.month, 1).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
    return String(currentYear);
  })();

  const viewOptions: { value: CalendarViewMode; label: string }[] = [
    { value: 'schedule', label: 'Lista'   },
    { value: 'day',      label: 'Dia'     },
    { value: '4days',    label: '4 Dias'  },
    { value: 'week',     label: 'Semana'  },
    { value: 'month',    label: 'Mês'     },
    { value: 'year',     label: 'Ano'     },
  ];

  const selectedOption = viewOptions.find(o => o.value === calendarView)!;

  const gridDays = (() => {
    if (calendarView === 'day') return [new Date(`${date}T12:00:00`)];
    if (calendarView === '4days') {
      const start = new Date(`${date}T12:00:00`);
      return Array.from({ length: 4 }, (_, i) => {
        const d = new Date(start); d.setDate(d.getDate() + i); return d;
      });
    }
    if (calendarView === 'week') return getDaysOfWeek(currentWeekStart);
    return [];
  })();

  // Indexar reservas por data YYYY-MM-DD para lookup O(1)
  const bookingsByDate = useMemo(() => {
    const map = new Map<string, Booking[]>();
    bookings.forEach(b => {
      const dateStr = toLocalDateString(new Date(b.start_time));
      if (!map.has(dateStr)) {
        map.set(dateStr, []);
      }
      map.get(dateStr)!.push(b);
    });
    return map;
  }, [bookings]);

  // Indexar quantidade de reservas por ano/mês para visualização anual
  const bookingsByYearMonthCount = useMemo(() => {
    const map = new Map<string, number>();
    bookings.forEach(b => {
      const d = new Date(b.start_time);
      const key = `${d.getFullYear()}-${d.getMonth()}`;
      map.set(key, (map.get(key) || 0) + 1);
    });
    return map;
  }, [bookings]);

  const bookingsForSelectedDate = useMemo(() => {
    return bookingsByDate.get(date) || [];
  }, [bookingsByDate, date]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[70vh] bg-background">
        <Loader2 className="animate-spin text-primary-500 mb-4" size={48} />
        <p className="text-neutral-500 text-lg font-medium">Carregando painel do coworking...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 pb-16">
      <div className="bg-primary-900 text-white py-12 px-6 shadow-md relative overflow-hidden">
        <div className="absolute right-0 top-0 opacity-10 pointer-events-none transform translate-x-12 -translate-y-12">
          <Sparkles size={300} />
        </div>
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center justify-between gap-6 relative z-10">
          <div>
            <div className="flex items-center gap-2 mb-2 text-primary-300">
              <MapPin size={18} />
              <span className="font-semibold text-sm tracking-wider uppercase">Endereço: A5 Offices, Bloco 1, Sala 252</span>
            </div>
            <div className="flex items-center gap-2">
              <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">Clínica Psi Mazulo</h1>
              {isAdmin && (
                <span className="bg-primary-500/30 text-primary-100 border border-primary-500/50 text-xs font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                  <Shield size={12} /> Admin
                </span>
              )}
            </div>
            <p className="text-primary-100 mt-2 text-lg">
              Olá, <span className="font-bold text-white">{profile?.full_name || currentUser?.email}</span>. Bem-vindo(a) à agenda compartilhada da clínica.
            </p>
          </div>
          <button onClick={handleLogout} className="self-start md:self-center flex items-center gap-2 bg-primary-700 hover:bg-primary-600 transition-colors text-white font-semibold py-2.5 px-5 rounded-xl shadow-md">
            <LogOut size={18} /> Sair
          </button>
        </div>
      </div>

      {isAdmin && (
        <div className="max-w-7xl mx-auto px-6 mt-8">
          <div className="bg-white p-2 rounded-2xl shadow-sm border border-neutral-100 flex gap-2 max-w-md">
            <button onClick={() => setViewMode('agenda')}
              className={`flex-1 py-2 px-4 rounded-xl font-semibold text-sm transition-all duration-200 flex items-center justify-center gap-2 ${viewMode === 'agenda' ? 'bg-primary-900 text-white shadow-sm' : 'text-neutral-600 hover:bg-neutral-50'}`}>
              <CalendarIcon size={16} /> Agenda da Sala
            </button>
            <button onClick={() => { setViewMode('admin'); fetchPendingProfiles(); }}
              className={`flex-1 py-2 px-4 rounded-xl font-semibold text-sm transition-all duration-200 flex items-center justify-center gap-2 relative ${viewMode === 'admin' ? 'bg-primary-900 text-white shadow-sm' : 'text-neutral-600 hover:bg-neutral-50'}`}>
              <Users size={16} /> Novas Contas
              {pendingProfiles.length > 0 && (
                <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
                  {pendingProfiles.length}
                </span>
              )}
            </button>
          </div>
        </div>
      )}

      {viewMode === 'admin' && isAdmin ? (
        <div className="max-w-7xl mx-auto px-6 mt-8">
          <div className="bg-white p-8 rounded-3xl shadow-md border border-neutral-100 min-h-[400px]">
            <div className="border-b border-neutral-100 pb-5 mb-6">
              <h2 className="text-2xl font-bold text-primary-900 flex items-center gap-2">
                <Users size={24} className="text-primary-600" /> Novas Contas Aguardando Aprovação
              </h2>
              <p className="text-neutral-500 text-sm mt-1">Profissionais que se cadastraram e precisam de liberação.</p>
            </div>
            {pendingProfiles.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center text-neutral-400 bg-neutral-50 rounded-2xl border border-dashed border-neutral-200">
                <Users size={48} className="mb-3 text-neutral-300" />
                <p className="font-semibold text-lg text-neutral-600">Tudo limpo por aqui!</p>
                <p className="text-sm mt-1">Não há novos pedidos de cadastro pendentes no momento.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {pendingProfiles.map(p => (
                  <div key={p.id} className="bg-neutral-50 border border-neutral-200 rounded-2xl p-5 flex flex-col justify-between hover:shadow-md transition-shadow duration-150">
                    <div className="space-y-3">
                      <div>
                        <span className="text-xs text-neutral-400 font-semibold block uppercase tracking-wider">Nome Completo</span>
                        <h4 className="font-bold text-neutral-800 text-lg leading-snug">{p.full_name || 'Sem nome informado'}</h4>
                      </div>
                      <div>
                        <span className="text-xs text-neutral-400 font-semibold block uppercase tracking-wider">E-mail</span>
                        <p className="text-neutral-600 text-sm font-medium break-all">{p.email}</p>
                      </div>
                      <div>
                        <span className="text-xs text-neutral-400 font-semibold block uppercase tracking-wider">Cadastrado em</span>
                        <p className="text-neutral-600 text-xs">{formatDateLabel(new Date(p.created_at))}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 mt-6 border-t border-neutral-200/60 pt-4">
                      <button onClick={() => handleRejectProfile(p.id)} disabled={actionLoading}
                        className="flex items-center justify-center gap-1 border border-red-200 hover:bg-red-50 text-red-600 font-semibold py-2 px-3 rounded-xl text-xs transition-colors duration-150 disabled:opacity-50">
                        <X size={14} /> Recusar
                      </button>
                      <button onClick={() => handleApproveProfile(p.id)} disabled={actionLoading}
                        className="flex items-center justify-center gap-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-3 rounded-xl text-xs shadow-sm hover:shadow transition-all duration-150 disabled:opacity-50">
                        <Check size={14} /> Aprovar Acesso
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="max-w-7xl mx-auto px-6 mt-10 grid grid-cols-1 lg:grid-cols-4 gap-8">
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white p-6 rounded-3xl shadow-md border border-neutral-100">
              <h2 className="text-xl font-bold text-primary-900 mb-5 flex items-center gap-2">
                <Info className="text-primary-500" size={22} /> Informações
              </h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="bg-primary-50 p-2.5 rounded-xl text-primary-700 mt-0.5"><Wifi size={18} /></div>
                  <div>
                    <h4 className="font-semibold text-neutral-800 text-sm">Rede Wi-Fi</h4>
                    <p className="text-neutral-600 text-sm">MazuloNeuroPsi</p>
                    <p className="text-neutral-500 text-xs">Senha: <code className="bg-neutral-100 px-1.5 py-0.5 rounded font-mono font-semibold">Psiclinica2212</code></p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="bg-primary-50 p-2.5 rounded-xl text-primary-700 mt-0.5"><Key size={18} /></div>
                  <div>
                    <h4 className="font-semibold text-neutral-800 text-sm">Acesso / Chaves</h4>
                    <p className="text-neutral-600 text-xs leading-relaxed">Solicitar senha da sala diretamente com a Psi Gabrielle Mazulo</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="bg-primary-50 p-2.5 rounded-xl text-primary-700 mt-0.5"><Wind size={18} /></div>
                  <div>
                    <h4 className="font-semibold text-neutral-800 text-sm">Ar Condicionado</h4>
                    <p className="text-neutral-600 text-xs leading-relaxed">Mantenha em 22°C. Ao sair, desligue o aparelho e feche as janelas.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="bg-primary-50 p-2.5 rounded-xl text-primary-700 mt-0.5"><Coffee size={18} /></div>
                  <div>
                    <h4 className="font-semibold text-neutral-800 text-sm">Copa e Organização</h4>
                    <p className="text-neutral-600 text-xs leading-relaxed">Cafeteira na copa. Lave as xícaras utilizadas e recolha seus descartáveis.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-primary-50 p-6 rounded-3xl border border-primary-100">
              <h3 className="font-bold text-primary-900 mb-2 flex items-center gap-1.5">
                <Heart className="text-primary-600 fill-primary-600" size={18} /> Suporte
              </h3>
              <p className="text-neutral-700 text-xs leading-relaxed">Dúvidas ou problemas com limpeza/insumos? Envie mensagem no grupo de sublocadores.</p>
            </div>
          </div>

          <div className="lg:col-span-3 bg-white rounded-3xl shadow-md border border-neutral-100 p-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 pb-4 border-b border-neutral-100">
              <div className="flex items-center gap-2">
                <CalendarDays className="text-primary-600 flex-shrink-0" size={24} />
                <h3 className="text-lg font-bold text-neutral-800 capitalize">{calendarTitle}</h3>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button onClick={() => { setFormError(''); setFormSuccess(''); setIsBookingModalOpen(true); }}
                  className="bg-primary-700 hover:bg-primary-900 text-white font-bold py-1.5 px-4 rounded-xl text-xs flex items-center gap-1.5 shadow-sm hover:shadow transition-all duration-150">
                  <Plus size={14} /> Nova Reserva
                </button>
                {/* Dropdown de Visualização */}
                <div className="relative">
                  <button
                    type="button"
                    aria-haspopup="menu"
                    aria-expanded={showViewDropdown}
                    aria-controls="cowork-view-menu"
                    onClick={() => setShowViewDropdown(v => !v)}
                    className="flex items-center gap-1.5 border border-neutral-300 hover:bg-neutral-50 font-semibold text-xs rounded-xl px-3 py-2 transition-colors">
                    <CalendarDays size={13} />
                    <span>{selectedOption.label}</span>
                    <ChevronDown size={13} className={`transition-transform duration-150 ${showViewDropdown ? 'rotate-180' : ''}`} />
                  </button>
                  {showViewDropdown && (
                    <div
                      id="cowork-view-menu"
                      role="menu"
                      aria-label="Opções de visualização"
                      className="absolute right-0 top-full mt-1 bg-white border border-neutral-200 rounded-2xl shadow-xl z-50 py-1.5 min-w-[140px]">
                      {viewOptions.map(opt => (
                        <button key={opt.value}
                          type="button"
                          role="menuitem"
                          onClick={() => {
                            const d = new Date(`${date}T12:00:00`);
                            setCalendarView(opt.value);
                            if (opt.value === 'week') setCurrentWeekStart(getMonday(d));
                            if (opt.value === 'month') setCurrentMonth({ year: d.getFullYear(), month: d.getMonth() });
                            if (opt.value === 'year') setCurrentYear(d.getFullYear());
                            setShowViewDropdown(false);
                          }}
                          className={`w-full text-left px-4 py-2 text-sm font-semibold flex items-center gap-2 transition-colors ${calendarView === opt.value ? 'bg-primary-50 text-primary-900' : 'text-neutral-700 hover:bg-neutral-50'}`}>
                          {opt.label}
                          {calendarView === opt.value && <Check size={13} className="ml-auto text-primary-600" />}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={handleToday}
                    className="px-4 py-2 border border-neutral-300 hover:bg-neutral-50 font-semibold text-xs rounded-xl transition-colors">
                    Hoje
                  </button>
                  <div className="flex items-center gap-0.5">
                    <button onClick={handlePrev} className="p-2 border border-neutral-300 hover:bg-neutral-50 rounded-xl transition-colors" title="Anterior">
                      <ChevronLeft size={18} />
                    </button>
                    <button onClick={handleNext} className="p-2 border border-neutral-300 hover:bg-neutral-50 rounded-xl transition-colors" title="Próximo">
                      <ChevronRight size={18} />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {(calendarView === 'day' || calendarView === '4days' || calendarView === 'week') && (
              <>
                <div className="w-full overflow-x-auto">
                  <div style={{ minWidth: gridDays.length === 1 ? '400px' : gridDays.length <= 4 ? '700px' : '900px' }} className="relative">
                    {gridDays.length > 1 && (
                      <div className="flex border-b border-neutral-200 pb-2 text-center mb-1">
                        <div className="w-16 flex-shrink-0" />
                        {gridDays.map((day, idx) => {
                          const isToday = isSameDay(day, new Date());
                          const name = day.toLocaleDateString('pt-BR', { weekday: 'short' });
                          const num = day.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
                          return (
                            <div key={idx} className={`flex-1 py-2 rounded-xl text-sm font-bold flex flex-col items-center mx-0.5 ${isToday ? 'bg-primary-100 text-primary-900 border border-primary-300' : 'text-neutral-700'}`}>
                              <span className="capitalize">{name}</span>
                              <span className="text-xs text-neutral-500 font-medium">{num}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    <div className="flex relative h-[1040px]">
                      <div className="w-16 flex-shrink-0 pr-2 text-right text-neutral-400 font-semibold text-xs relative select-none">
                        {gridHours.map((h, i) => (
                          <div key={i} className="h-[80px] flex items-start justify-end -translate-y-2.5">{h}</div>
                        ))}
                        <div className="absolute bottom-0 right-2 -translate-y-2.5">22:00</div>
                      </div>
                      {gridDays.map((day, dayIdx) => {
                        const dayStr = toLocalDateString(day);
                        const dayBookings = bookingsByDate.get(dayStr) || [];
                        const isToday = isSameDay(day, new Date());
                        return (
                          <div key={dayIdx} className="relative h-full border-l border-neutral-200 flex-1 min-w-0">
                            {gridHours.map((hour, idx) => (
                              <div key={idx} onClick={() => handleCellClick(day, hour)}
                                className={`h-[80px] border-b border-dashed border-neutral-100 transition-all duration-100 cursor-pointer ${isToday ? 'hover:bg-primary-50/50' : 'hover:bg-neutral-50/70'}`} />
                            ))}
                            <div className="absolute inset-0 pointer-events-none">
                              {dayBookings.map(b => {
                                const isOwner = currentUser?.id === b.user_id;
                                const canDelete = isOwner || isAdmin;
                                const layout = getBookingLayoutStyles(b);
                                return (
                                  <div key={b.id} style={layout}
                                    className={`absolute left-1 right-1 pointer-events-auto rounded-xl px-2.5 py-2 font-semibold flex flex-col justify-start shadow-md border-l-4 transition-all overflow-hidden hover:z-20 hover:shadow-xl ${isOwner ? 'bg-primary-100 text-primary-900 border-primary-500' : 'bg-white text-neutral-800 border-neutral-300 border shadow-sm'}`}>
                                    <div className="space-y-0.5">
                                      <div className="flex items-center gap-1 font-bold text-neutral-500 text-[10px] uppercase tracking-wider">
                                        <Clock size={10} />
                                        <span>{formatTimeLabel(b.start_time)} — {formatTimeLabel(b.end_time)}</span>
                                        {b.recurrence_group_id && (
                                          <Repeat size={10} className="text-primary-600 ml-1 inline animate-pulse" />
                                        )}
                                      </div>
                                      <div className="font-extrabold text-neutral-900 text-sm leading-snug truncate">{b.title}</div>
                                      <div className="text-xs text-neutral-600 font-medium flex items-center gap-1 truncate">
                                        <User size={11} className="text-neutral-400 flex-shrink-0" />
                                        <span className="truncate">{b.user_name}</span>
                                      </div>
                                    </div>
                                    {canDelete && (
                                      <button onClick={e => { e.stopPropagation(); handleDeleteBooking(b.id, b.recurrence_group_id); }} disabled={actionLoading}
                                        className="absolute bottom-1 right-1 text-red-500 hover:text-red-700 hover:bg-red-50 p-1 rounded-lg transition-colors disabled:opacity-50"
                                        title={isAdmin && !isOwner ? 'Excluir (Admin)' : 'Cancelar Reserva'}>
                                        <Trash2 size={12} />
                                      </button>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </>
            )}

            {calendarView === 'schedule' && (
              <div className="space-y-4 max-w-3xl mx-auto py-2">
                {bookingsForSelectedDate.length === 0 ? (
                  <div onClick={() => setIsBookingModalOpen(true)}
                    className="flex flex-col items-center justify-center py-16 text-center text-neutral-400 bg-neutral-50 rounded-3xl border border-dashed border-neutral-200 cursor-pointer hover:bg-neutral-100/50 transition-colors">
                    <CalendarIcon size={48} className="mb-3 text-neutral-300" />
                    <p className="font-semibold text-lg text-neutral-600">Nenhum agendamento para este dia.</p>
                    <p className="text-sm mt-1">Clique aqui para fazer uma reserva.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {bookingsForSelectedDate.map(booking => {
                      const isOwner = currentUser?.id === booking.user_id;
                      const canDelete = isOwner || isAdmin;
                      return (
                        <div key={booking.id}
                          className={`p-5 rounded-2xl border transition-all duration-150 flex items-center justify-between gap-6 hover:shadow-md ${isOwner ? 'bg-primary-50/60 border-primary-200 shadow-sm' : 'bg-white border-neutral-200'}`}>
                          <div className="flex items-center gap-4">
                            <div className="bg-primary-100 text-primary-800 p-3 rounded-full flex-shrink-0 font-bold text-sm uppercase w-10 h-10 flex items-center justify-center border border-primary-200">
                              {booking.user_name.substring(0, 2)}
                            </div>
                            <div className="space-y-1">
                              <div className="flex items-center gap-1.5 text-xs text-neutral-500 font-semibold">
                                <Clock size={14} className="text-neutral-400" />
                                <span>{formatTimeLabel(booking.start_time)} - {formatTimeLabel(booking.end_time)}</span>
                                {booking.recurrence_group_id && (
                                  <span className="flex items-center gap-0.5 bg-primary-100 text-primary-800 text-[9px] px-1.5 py-0.5 rounded-full font-bold">
                                    <Repeat size={10} /> Recorrente
                                  </span>
                                )}
                              </div>
                              <h4 className="font-bold text-neutral-800 text-base leading-snug">{booking.title}</h4>
                              <p className="text-xs text-neutral-600 flex items-center gap-1">
                                <span className="font-semibold text-primary-800">Responsável:</span> {booking.user_name}
                              </p>
                            </div>
                          </div>
                          {canDelete && (
                            <button onClick={() => handleDeleteBooking(booking.id, booking.recurrence_group_id)} disabled={actionLoading}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2.5 rounded-xl transition-colors flex-shrink-0 disabled:opacity-50 border border-transparent hover:border-red-100"
                              title={isAdmin && !isOwner ? 'Excluir (Admin)' : 'Cancelar Reserva'}>
                              <Trash2 size={18} />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {calendarView === 'month' && (() => {
              const { year, month } = currentMonth;
              const firstDay = new Date(year, month, 1).getDay();
              const startOffset = firstDay === 0 ? 6 : firstDay - 1;
              const daysInMonth = getDaysInMonth(year, month);
              const cells: (number | null)[] = [
                ...Array(startOffset).fill(null),
                ...Array.from({ length: daysInMonth }, (_, i) => i + 1)
              ];
              while (cells.length % 7 !== 0) cells.push(null);
              const weekDayNames = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];
              const todayStr = toLocalDateString(new Date());
              return (
                <div>
                  <div className="grid grid-cols-7 mb-2">
                    {weekDayNames.map(wd => (
                      <div key={wd} className="text-center text-xs font-bold text-neutral-500 uppercase tracking-wider py-2">{wd}</div>
                    ))}
                  </div>
                  <div className="grid grid-cols-7 gap-1">
                    {cells.map((day, idx) => {
                      if (day === null) return <div key={idx} className="h-24 rounded-xl bg-neutral-50/50" />;
                      const cellStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                      const isToday = cellStr === todayStr;
                      const isSelected = cellStr === date;
                      const cellBookings = bookingsByDate.get(cellStr) || [];
                      return (
                        <div key={idx} onClick={() => { setDate(cellStr); setCalendarView('day'); }}
                          className={`h-24 rounded-xl p-2 cursor-pointer transition-all duration-150 border hover:shadow-md ${isToday ? 'bg-primary-50 border-primary-300' : isSelected ? 'bg-neutral-100 border-neutral-300' : 'bg-white border-neutral-100 hover:border-neutral-300'}`}>
                          <span className={`text-sm font-bold inline-flex items-center justify-center w-6 h-6 rounded-full ${isToday ? 'bg-primary-700 text-white' : 'text-neutral-700'}`}>
                            {day}
                          </span>
                          <div className="mt-1 space-y-0.5 overflow-hidden">
                            {cellBookings.slice(0, 3).map(b => {
                              const isOwner = currentUser?.id === b.user_id;
                              return (
                                <div key={b.id} className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-md truncate leading-tight ${isOwner ? 'bg-primary-200 text-primary-900' : 'bg-neutral-200 text-neutral-700'}`}>
                                  {formatTimeLabel(b.start_time)} {b.title}
                                </div>
                              );
                            })}
                            {cellBookings.length > 3 && (
                              <div className="text-[9px] text-neutral-500 font-semibold pl-1">+{cellBookings.length - 3} mais</div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {calendarView === 'year' && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {Array.from({ length: 12 }, (_, monthIdx) => {
                  const monthName = new Date(currentYear, monthIdx, 1).toLocaleDateString('pt-BR', { month: 'long' });
                  const firstDay = new Date(currentYear, monthIdx, 1).getDay();
                  const startOffset = firstDay === 0 ? 6 : firstDay - 1;
                  const daysInM = getDaysInMonth(currentYear, monthIdx);
                  const cells: (number | null)[] = [
                    ...Array(startOffset).fill(null),
                    ...Array.from({ length: daysInM }, (_, i) => i + 1)
                  ];
                  while (cells.length % 7 !== 0) cells.push(null);
                  const todayStr = toLocalDateString(new Date());
                  const isCurrentMonth = monthIdx === new Date().getMonth() && currentYear === new Date().getFullYear();
                  
                  const monthBookingsCount = bookingsByYearMonthCount.get(`${currentYear}-${monthIdx}`) || 0;
                  return (
                    <div key={monthIdx}
                      onClick={() => { setCurrentMonth({ year: currentYear, month: monthIdx }); setCalendarView('month'); }}
                      className={`rounded-2xl p-4 border cursor-pointer hover:shadow-lg transition-all duration-150 ${isCurrentMonth ? 'border-primary-300 bg-primary-50/40' : 'border-neutral-200 bg-white hover:border-neutral-300'}`}>
                      <h4 className={`text-xs font-extrabold uppercase tracking-wider mb-2 capitalize ${isCurrentMonth ? 'text-primary-800' : 'text-neutral-600'}`}>
                        {monthName}
                      </h4>
                      <div className="grid grid-cols-7 mb-1">
                        {['S','T','Q','Q','S','S','D'].map((d, i) => (
                          <div key={i} className="text-center text-[8px] font-bold text-neutral-400">{d}</div>
                        ))}
                      </div>
                      <div className="grid grid-cols-7 gap-px">
                        {cells.map((day, cIdx) => {
                          if (day === null) return <div key={cIdx} className="h-5" />;
                          const cellStr = `${currentYear}-${String(monthIdx + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                          const isToday = cellStr === todayStr;
                          const hasBooking = bookingsByDate.has(cellStr);
                          return (
                            <div key={cIdx} className="h-5 flex items-center justify-center relative">
                              <span className={`text-[9px] font-semibold w-4 h-4 flex items-center justify-center rounded-full ${isToday ? 'bg-primary-700 text-white' : 'text-neutral-600'}`}>
                                {day}
                              </span>
                              {hasBooking && !isToday && (
                                <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 bg-primary-500 rounded-full" />
                              )}
                            </div>
                          );
                        })}
                      </div>
                      {monthBookingsCount > 0 && (
                        <div className="mt-2 text-[9px] text-primary-700 font-bold">
                          {monthBookingsCount} reserva{monthBookingsCount > 1 ? 's' : ''}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

          </div>
        </div>
      )}

      {isBookingModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-xs flex items-center justify-center z-50 p-4 transition-all">
          <div className="bg-white rounded-3xl shadow-2xl border border-neutral-100 max-w-md w-full p-6 relative">
            <button onClick={() => setIsBookingModalOpen(false)}
              className="absolute top-4 right-4 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 p-1.5 rounded-xl transition-all">
              <X size={20} />
            </button>
            <h2 className="text-xl font-bold text-primary-900 mb-5 flex items-center gap-2">
              <CalendarIcon className="text-primary-500" size={22} /> Reservar Horário (Clínica)
            </h2>
            {formError && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl text-sm mb-5 flex items-start gap-2">
                <AlertTriangle className="flex-shrink-0 mt-0.5" size={18} /> <span>{formError}</span>
              </div>
            )}
            {formSuccess && (
              <div className="bg-green-50 border border-green-200 text-green-800 p-4 rounded-xl text-sm mb-5">{formSuccess}</div>
            )}
            <form onSubmit={handleAddBooking} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-neutral-700 mb-1.5">Profissional</label>
                <div className="flex items-center gap-2 bg-neutral-50 px-4 py-2.5 rounded-xl border border-neutral-200 text-neutral-600 text-sm font-medium">
                  <User size={16} /> {profile?.full_name || currentUser?.email}
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-neutral-700 mb-1.5">Data da Reserva</label>
                <input type="date" required value={date}
                  onChange={e => { setDate(e.target.value); setFormSuccess(''); setFormError(''); }}
                  className="w-full px-4 py-2.5 rounded-xl border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-500 transition-all text-neutral-800 text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-neutral-700 mb-1.5">Início</label>
                  <select value={startTime} onChange={e => { setStartTime(e.target.value); setFormSuccess(''); setFormError(''); }}
                    className="w-full px-3 py-2.5 rounded-xl border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-500 transition-all text-neutral-800 text-sm bg-white">
                    {dropdownTimes.slice(0, -1).map(t => <option key={`start-${t}`} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-neutral-700 mb-1.5">Término</label>
                  <select value={endTime} onChange={e => { setEndTime(e.target.value); setFormSuccess(''); setFormError(''); }}
                    className="w-full px-3 py-2.5 rounded-xl border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-500 transition-all text-neutral-800 text-sm bg-white">
                    {dropdownTimes.slice(1).map(t => <option key={`end-${t}`} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-neutral-700 mb-1.5">Descrição / Nota (Opcional)</label>
                <input type="text" value={title} onChange={e => setTitle(e.target.value)}
                  placeholder="Ex: Atendimento Clínico Infantil" maxLength={60}
                  className="w-full px-4 py-2.5 rounded-xl border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-500 transition-all text-neutral-800 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-neutral-700 mb-1.5">Recorrência</label>
                <select value={recurrence} onChange={e => setRecurrence(e.target.value as 'none' | 'weekly' | 'biweekly')}
                  className="w-full px-3 py-2.5 rounded-xl border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-500 transition-all text-neutral-800 text-sm bg-white">
                  <option value="none">Sem recorrência (Única)</option>
                  <option value="weekly">Semanal (12 semanas)</option>
                  <option value="biweekly">Quinzenal (6 ocorrências)</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3 pt-2">
                <button type="button" onClick={() => setIsBookingModalOpen(false)}
                  className="w-full border border-neutral-300 hover:bg-neutral-50 text-neutral-700 font-bold py-2.5 px-4 rounded-xl text-sm transition-all">
                  Cancelar
                </button>
                <button type="submit" disabled={actionLoading}
                  className="w-full flex justify-center items-center gap-2 bg-primary-700 hover:bg-primary-900 text-white font-bold py-2.5 px-4 rounded-xl text-sm transition-all duration-200 disabled:opacity-50">
                  {actionLoading ? <Loader2 className="animate-spin" size={18} /> : 'Reservar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL DE CONFIRMAÇÃO DE EXCLUSÃO DE RECORRÊNCIA */}
      {deleteModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-xs flex items-center justify-center z-50 p-4 transition-all">
          <div className="bg-white rounded-3xl shadow-2xl border border-neutral-100 max-w-sm w-full p-6 relative">
            <h3 className="text-lg font-bold text-neutral-800 mb-4">Esta é uma reserva recorrente</h3>
            <p className="text-sm text-neutral-600 mb-6">Deseja cancelar apenas a reserva deste dia ou de toda a série recorrente criada?</p>
            <div className="space-y-2">
              <button
                onClick={() => confirmDeleteSingle(deleteModal.bookingId)}
                className="w-full bg-neutral-100 hover:bg-neutral-200 text-neutral-800 font-bold py-2.5 px-4 rounded-xl text-sm transition-all"
              >
                Cancelar apenas este dia
              </button>
              <button
                onClick={() => confirmDeleteAll(deleteModal.groupId!)}
                className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-2.5 px-4 rounded-xl text-sm transition-all"
              >
                Cancelar toda a série
              </button>
              <button
                onClick={() => setDeleteModal(null)}
                className="w-full border border-neutral-300 hover:bg-neutral-50 text-neutral-700 font-semibold py-2 px-4 rounded-xl text-xs transition-all mt-2"
              >
                Voltar
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
